import Dexie, { type Table } from 'dexie';
import type { VisitaBundle } from './schemas';

// Persistência offline da visita técnica (IndexedDB via Dexie). Três tabelas:
//  - bundles: pacote da fazenda (polígonos + safra) cacheado pra trabalhar offline
//  - visitas: cabeçalho da sessão enfileirada
//  - fotos:   blob + metadados (GPS, talhão resolvido, legenda, recomendação)
// A fila é drenada pelo sync quando o device volta a ter internet.

export interface CachedBundle {
  imovelId: number;
  bundle: VisitaBundle;
  savedAt: number;
}

export type VisitaStatus = 'rascunho' | 'pendente' | 'sincronizada';

export interface PendingVisita {
  clientUuid: string;
  imovelId: number | null;
  // Visita multi-fazenda (feita pela tela do produtor). `imovelId` continua
  // sendo a fazenda principal — é por ela que o rascunho é reencontrado.
  imovelIds?: number[];
  fazendaNome: string | null;
  produtorNome: string | null;
  agronomoNome: string | null;
  dataVisita: string; // ISO
  observacoes: string;
  status: VisitaStatus;
  createdAt: number;
  syncedVisitaId: number | null;
}

// Um áudio gravado sobre a foto. São vários por foto — o agrônomo volta na
// mesma imagem e grava outra observação.
export interface PendingAudio {
  clientUuid: string;
  blob: Blob;
  mime: string;
  filename: string;
  durMs: number | null;
}

// Ocorrência apontada a campo. Hoje o app registra só praga, com a quantidade
// contada no ponto — é o que vira soma/média por talhão no relatório.
export interface OcorrenciaCampo {
  clientUuid: string;
  tipo: string;
  nome: string;
  quantidade: number | null;
  unidade: string | null;
  severidade: string | null;
}

export interface PendingFoto {
  clientUuid: string;
  visitaUuid: string;
  // Ponto a que esta foto pertence (agrupamento por GPS → galeria no relatório).
  pontoUuid?: string | null;
  // Ponto marcado SEM foto (só ocorrência/anotação): blob ausente e semFoto true.
  blob?: Blob | null;
  semFoto?: boolean;
  mime: string;
  filename: string;
  lat: number | null;
  lon: number | null;
  capturadoEm: string; // ISO
  talhaoId: number | null;
  talhaoNumero: string | null;
  legenda: string[];
  estadio: string | null;
  recomendacao: string;
  // Áudios do agrônomo (offline) — vão junto no sync.
  audios?: PendingAudio[];
  // Ocorrências apontadas no ponto (ficam na 1ª foto do ponto).
  ocorrencias?: OcorrenciaCampo[];
  // Áudio único das versões antigas do app — lido no sync como fallback.
  audioBlob?: Blob | null;
  audioMime?: string | null;
  audioFilename?: string | null;
  audioDurMs?: number | null;
  synced: boolean;
  createdAt: number;
}

export interface Medicao {
  mip_item_id: number | null;
  item_nome: string | null;
  fase: string | null;   // 'adulto' | 'ninfa' | 'ovo' | 'larva' | null
  valor: number;
  unidade: string | null;
}

// Ponto da visita (agrupa N fotos por GPS). Carrega a recomendação (observacao)
// e o áudio do PONTO; as fotos referenciam via PendingFoto.pontoUuid. Continua
// servindo de ponto de amostragem MIP (medicoes), quando houver.
export interface PendingAmostra {
  clientUuid: string;
  visitaUuid: string;
  lat: number | null;
  lon: number | null;
  capturadoEm: string;
  observacao: string; // recomendação do agrônomo para o ponto
  talhaoId: number | null;
  talhaoNumero: string | null;
  estadio?: string | null;
  fotoClientUuid: string | null; // legado: foto única do ponto
  // Recomendação por áudio do ponto (offline) — vai junto no sync.
  audioBlob?: Blob | null;
  audioMime?: string | null;
  audioFilename?: string | null;
  audioDurMs?: number | null;
  medicoes: Medicao[];
  synced: boolean;
  createdAt: number;
}

// Cache de leitura (navegação offline): resposta de GET por path da API.
export interface ApiCacheRow {
  path: string;
  data: unknown;
  ts: number;
}

// Mapa estático (satélite + polígonos) da fazenda, pré-renderizado na sync p/ uso offline.
export interface MapaCache {
  imovelId: number;
  blob: Blob;
  savedAt: number;
}

// Tile de satélite (Esri) cacheado p/ o mapa interativo offline. id = "z/x/y".
export interface TileRow {
  id: string;
  blob: Blob;
}

class VisitaDB extends Dexie {
  bundles!: Table<CachedBundle, number>;
  visitas!: Table<PendingVisita, string>;
  fotos!: Table<PendingFoto, string>;
  amostras!: Table<PendingAmostra, string>;
  apiCache!: Table<ApiCacheRow, string>;
  mapas!: Table<MapaCache, number>;
  tiles!: Table<TileRow, string>;

  constructor() {
    super('hub-visita');
    this.version(1).stores({
      bundles: 'imovelId',
      visitas: 'clientUuid, imovelId, status',
      fotos: 'clientUuid, visitaUuid, synced',
    });
    // v2: pontos de amostragem com contagens MIP.
    this.version(2).stores({
      bundles: 'imovelId',
      visitas: 'clientUuid, imovelId, status',
      fotos: 'clientUuid, visitaUuid, synced',
      amostras: 'clientUuid, visitaUuid, synced',
    });
    // v3: cache de leitura p/ navegar produtores/fazendas/talhões offline.
    this.version(3).stores({
      bundles: 'imovelId',
      visitas: 'clientUuid, imovelId, status',
      fotos: 'clientUuid, visitaUuid, synced',
      amostras: 'clientUuid, visitaUuid, synced',
      apiCache: 'path',
    });
    // v4: mapa estático (satélite+polígonos) por fazenda p/ ver offline.
    this.version(4).stores({
      bundles: 'imovelId',
      visitas: 'clientUuid, imovelId, status',
      fotos: 'clientUuid, visitaUuid, synced',
      amostras: 'clientUuid, visitaUuid, synced',
      apiCache: 'path',
      mapas: 'imovelId',
    });
    // v5: tiles de satélite p/ o mapa interativo offline (pan/zoom).
    this.version(5).stores({
      bundles: 'imovelId',
      visitas: 'clientUuid, imovelId, status',
      fotos: 'clientUuid, visitaUuid, synced',
      amostras: 'clientUuid, visitaUuid, synced',
      apiCache: 'path',
      mapas: 'imovelId',
      tiles: 'id',
    });
  }
}

export const vdb = new VisitaDB();

// ── Bundle ───────────────────────────────────────────────────────────────
export async function saveBundle(imovelId: number, bundle: VisitaBundle): Promise<void> {
  await vdb.bundles.put({ imovelId, bundle, savedAt: Date.now() });
}
export async function getBundle(imovelId: number): Promise<VisitaBundle | null> {
  const r = await vdb.bundles.get(imovelId);
  return r?.bundle ?? null;
}

// ── Cache de leitura (navegação offline) ────────────────────────────────────
export async function putApiCache(path: string, data: unknown): Promise<void> {
  await vdb.apiCache.put({ path, data, ts: Date.now() });
}
export async function getApiCache(path: string): Promise<unknown> {
  return (await vdb.apiCache.get(path))?.data;
}
export interface PropsSyncInfo {
  ts: number;
  produtores: number;
  fazendas: number;
}
export async function getPropsSyncInfo(cpf?: string): Promise<PropsSyncInfo | null> {
  const key = cpf ? `__props_sync_${cpf.replace(/\D/g, '')}__` : '__props_sync__';
  const r = await vdb.apiCache.get(key);
  return (r?.data as PropsSyncInfo) ?? null;
}

// ── Mapa estático offline ────────────────────────────────────────────────────
export async function saveMapa(imovelId: number, blob: Blob): Promise<void> {
  await vdb.mapas.put({ imovelId, blob, savedAt: Date.now() });
}
export async function getMapa(imovelId: number): Promise<Blob | null> {
  return (await vdb.mapas.get(imovelId))?.blob ?? null;
}

// ── Tiles de satélite (mapa interativo offline) ──────────────────────────────
export async function getTile(id: string): Promise<Blob | null> {
  return (await vdb.tiles.get(id))?.blob ?? null;
}
export async function putTile(id: string, blob: Blob): Promise<void> {
  await vdb.tiles.put({ id, blob });
}
export async function hasTile(id: string): Promise<boolean> {
  return (await vdb.tiles.where('id').equals(id).count()) > 0;
}
// Zoom máximo cacheado por fazenda (pra o mapa offline não zoomar além disso).
export async function putFazMaxZoom(imovelId: number, maxZoom: number): Promise<void> {
  await vdb.apiCache.put({ path: `__tiles_${imovelId}__`, data: { maxZoom }, ts: Date.now() });
}
export async function getFazMaxZoom(imovelId: number): Promise<number | null> {
  const r = await vdb.apiCache.get(`__tiles_${imovelId}__`);
  return (r?.data as { maxZoom?: number } | undefined)?.maxZoom ?? null;
}

// ── Visita (sessão) ────────────────────────────────────────────────────────
export async function upsertVisita(v: PendingVisita): Promise<void> {
  await vdb.visitas.put(v);
}
export async function getVisita(clientUuid: string): Promise<PendingVisita | undefined> {
  return vdb.visitas.get(clientUuid);
}
// Visitas finalizadas mas ainda não sincronizadas (banner "Sincronizar").
// Opcionalmente filtradas a um conjunto de fazendas (imovelId).
export async function visitasPendentes(imovelIds?: number[]): Promise<PendingVisita[]> {
  const all = await vdb.visitas.where('status').equals('pendente').toArray();
  if (!imovelIds) return all;
  const set = new Set(imovelIds);
  return all.filter((v) => v.imovelId != null && set.has(v.imovelId));
}
export async function fotosDaVisita(visitaUuid: string): Promise<PendingFoto[]> {
  return vdb.fotos.where('visitaUuid').equals(visitaUuid).sortBy('createdAt');
}

// Visita pausada (rascunho) mais recente da fazenda — oferecida pra retomar
// ao entrar de novo em "Nova visita técnica". Rascunho NUNCA entra no sync
// (visitasPendentes filtra status 'pendente').
export async function rascunhoDaFazenda(imovelId: number): Promise<PendingVisita | null> {
  const rows = await vdb.visitas.where('status').equals('rascunho').toArray();
  const meus = rows.filter((v) => v.imovelId === imovelId).sort((a, b) => b.createdAt - a.createdAt);
  return meus[0] ?? null;
}

// Descarta a visita e tudo que ela carrega (fotos, áudios, amostras).
export async function descartarVisita(visitaUuid: string): Promise<void> {
  const fotos = await vdb.fotos.where('visitaUuid').equals(visitaUuid).primaryKeys();
  await vdb.fotos.bulkDelete(fotos);
  const amostras = await vdb.amostras.where('visitaUuid').equals(visitaUuid).primaryKeys();
  await vdb.amostras.bulkDelete(amostras);
  await vdb.visitas.delete(visitaUuid);
}

// ── Foto ─────────────────────────────────────────────────────────────────
export async function addFoto(f: PendingFoto): Promise<void> {
  await vdb.fotos.put(f);
}
export async function updateFoto(clientUuid: string, patch: Partial<PendingFoto>): Promise<void> {
  await vdb.fotos.update(clientUuid, patch);
}
export async function removeFoto(clientUuid: string): Promise<void> {
  await vdb.fotos.delete(clientUuid);
}

// Limpa fotos já sincronizadas de uma sessão (libera espaço no device).
export async function purgeSynced(visitaUuid: string): Promise<void> {
  const synced = await vdb.fotos.where('visitaUuid').equals(visitaUuid).filter((f) => f.synced).toArray();
  await vdb.fotos.bulkDelete(synced.map((f) => f.clientUuid));
}

// ── Amostra (ponto) ────────────────────────────────────────────────────────
export async function addAmostra(a: PendingAmostra): Promise<void> {
  await vdb.amostras.put(a);
}
export async function updateAmostra(clientUuid: string, patch: Partial<PendingAmostra>): Promise<void> {
  await vdb.amostras.update(clientUuid, patch);
}
export async function removeAmostra(clientUuid: string): Promise<void> {
  await vdb.amostras.delete(clientUuid);
}
export async function amostrasDaVisita(visitaUuid: string): Promise<PendingAmostra[]> {
  return vdb.amostras.where('visitaUuid').equals(visitaUuid).sortBy('createdAt');
}
